import React, { useState } from "react";
// TODO: Implement custom API client
import { useQuery } from "@tanstack/react-query";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import * as adminService from "@/shared/api/services/adminService";
import * as transactionService from "@/shared/api/services/transactionService";
import TransactionFilters from "@/shared/components/transactions/TransactionFilters";
import TransactionTable from "@/shared/components/transactions/TransactionTable";
import TransactionDetailPanel from "@/shared/components/admin/TransactionDetailPanel";
import { exportToCsv } from "@/shared/utils/csvExport";
import { Button } from "@/shared/components/ui/button";
import { ChevronLeft, ChevronRight, ArrowDownToLine } from "lucide-react";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { useEffect, useMemo } from "react";
import { mapGlobalTransactions } from "@/shared/utils/transactionMappers";
import * as providerService from "@/shared/api/services/providerService";

export default function Deposits() {
  const { t } = useLanguage();
  const [filters, setFilters] = useState({
    search: "",
    type: "all",
    engine: "all",
    reseller: "all",
    status: "all",
    merchant: "all",
    provider: "all",
    currency: "all",
    location: "all",
    dateFrom: undefined,
    dateTo: undefined,
  });
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [merchantIds, setMerchantIds] = useState([]);
  const pageSize = 10;

  const { data: paginationResult, isLoading: txLoading, error: transactionsError } = useQuery({
    queryKey: ["admin-global-transactions-deposits", page, pageSize, search, filters, merchantIds],
    queryFn: () => {
      const backendFilters = { type: "deposit" };
      if (filters.status !== "all") backendFilters.status = filters.status;
      if (filters.provider !== "all") backendFilters.provider = filters.provider;
      if (filters.currency !== "all") backendFilters.currency = filters.currency;
      if (filters.location !== "all") backendFilters.location = filters.location;
      if (filters.dateFrom) backendFilters.startDate = filters.dateFrom;
      if (filters.dateTo) {
        const endOfToDay = new Date(filters.dateTo);
        endOfToDay.setHours(23, 59, 59, 999);
        backendFilters.endDate = endOfToDay.toISOString();
      }

      return transactionService.getPaginatedTransactions(
        page + 1,
        pageSize,
        search,
        backendFilters,
        merchantIds
      );
    },
    refetchInterval: 5000,
    keepPreviousData: true,
  });

  const { data: providers_all = [] } = useQuery({
    queryKey: ["providers"],
    queryFn: () => providerService.getAllProviders(),
  });

  const { data: merchants = [] } = useQuery({
    queryKey: ["merchants"],
    queryFn: () => adminService.getAllMerchants(),
  });

  const { data: engines = [] } = useQuery({
    queryKey: ["engines"],
    queryFn: () => adminService.getAllEngines(),
  });

  const { data: resellers = [] } = useQuery({
    queryKey: ["resellers"],
    queryFn: () => adminService.getAllResellers(),
  });

  const { data: uniqueProviderNames = [] } = useQuery({
    queryKey: ["all-transaction-providers"],
    queryFn: () => transactionService.getUniqueProviders(),
  });

  const rawTransactions = paginationResult?.data || [];
  const totalTransactions = paginationResult?.total || 0;
  const totalPagesCount = paginationResult?.totalPages || 1;

  const combinedData = useMemo(() => {
    return mapGlobalTransactions(rawTransactions);
  }, [rawTransactions]);

  const providerOptions = useMemo(() => {
    const options = [];
    const seen = new Set();

    const addOption = (value, label, aliases = []) => {
      const normalizedValue = String(value || "").trim();
      if (!normalizedValue) return;
      if (seen.has(normalizedValue)) return;

      seen.add(normalizedValue);
      options.push({
        value: normalizedValue,
        label: label || normalizedValue,
        aliases: [normalizedValue, ...(aliases || [])].filter(Boolean),
      });
    };

    providers_all.forEach((p) => {
      const label = p.nameEn || p.name || p.code;
      addOption(p.id, label, [p.code, p.name, p.nameEn]);
    });

    combinedData.forEach((tx) => {
      const name = tx.client_bank_name || tx.bank_name || tx.provider_code || tx.bank_code;
      const provider = providers_all.find(p =>
        p.code === tx.provider_code ||
        p.name === name ||
        p.nameEn === name ||
        p.code === tx.bank_code
      );
      const value = provider ? provider.id : name;
      addOption(value, name, [tx.provider_code, tx.bank_code]);
    });

    uniqueProviderNames.forEach((entry) => {
      const name = typeof entry === "string"
        ? entry
        : (entry?.bankName || entry?.name || entry?.provider || entry?.value);

      const provider = providers_all.find(p =>
        p.name === name ||
        p.nameEn === name ||
        p.code === name
      );
      const value = provider ? provider.id : name;
      addOption(value, name);
    });

    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [providers_all, combinedData, uniqueProviderNames]);

  useEffect(() => {
    const hasEntityFilters =
      filters.engine !== "all" ||
      filters.reseller !== "all" ||
      filters.merchant !== "all";

    if (!hasEntityFilters) {
      setMerchantIds([]);
      return;
    }

    const merchantIdSet = new Set();

    if (filters.engine !== "all") {
      const resellerIdsUnderEngine = resellers
        .filter((r) => r.createdByEngineId === filters.engine)
        .map((r) => r.id);

      merchants
        .filter((m) => m.createdByEngineId === filters.engine || resellerIdsUnderEngine.includes(m.createdByResellerId))
        .forEach((m) => merchantIdSet.add(m.id));
    }

    if (filters.reseller !== "all") {
      merchants
        .filter((m) => m.createdByResellerId === filters.reseller)
        .forEach((m) => merchantIdSet.add(m.id));
    }

    if (filters.merchant !== "all") {
      merchantIdSet.add(filters.merchant);
    }

    const nextMerchantIds = Array.from(merchantIdSet);
    const normalizedIds = nextMerchantIds.length > 0 ? nextMerchantIds : ["NO_MERCHANT_FOUND"];

    setMerchantIds((prev) => {
      if (prev.length === normalizedIds.length && prev.every((id, index) => id === normalizedIds[index])) {
        return prev;
      }
      return normalizedIds;
    });
  }, [filters.engine, filters.reseller, filters.merchant, merchants, resellers]);

  const totalPages = totalPagesCount;

  // Stats
  const stats = React.useMemo(() => {
    const success = combinedData.filter((t) => t.status === "success");
    const pending = combinedData.filter((t) => t.status === "pending" || t.status === "processing");
    return {
      total: success.reduce((sum, t) => sum + (t.ammount || t.amount || 0), 0),
      count: totalTransactions,
      successCount: success.length,
      pendingCount: pending.length,
    };
  }, [combinedData, totalTransactions]);

  // CSV export
  const DEPOSIT_CSV_HEADERS = [
    "id",
    "payment_id",
    "reference",
    "ammount",
    "fee_amount",
    "net_ammount",
    "status",
    "currency",
    "merchant_id",
    "merchant_code",
    "merchant_name",
    "reseller_id",
    "reseller_code",
    "reseller_name",
    "provider_code",
    "client_bank_name",
    "client_name",
    "client_bank_account",
    "createdAt",
    "updatedAt",
    "type",
  ];

  const getDepositCellValue = (row, key) => {
    if (key === "merchant_name") return row.merchant_name ?? row.merchant?.name;
    if (key === "reseller_name") return row.reseller_name ?? row.reseller?.name;
    return row[key];
  };

  const handleExportCsv = () => {
    exportToCsv(combinedData, {
      headers: DEPOSIT_CSV_HEADERS,
      filename: `deposits-${new Date().toISOString().slice(0, 10)}.csv`,
      getCellValue: getDepositCellValue,
    });
  };

  const selectedMerchant = selectedTransaction
    ? (selectedTransaction.merchant?.id ? selectedTransaction.merchant : (selectedTransaction.merchant_id ? merchants.find((m) => m.id === selectedTransaction.merchant_id) : null))
    : null;
  const selectedReseller = selectedTransaction
    ? (selectedTransaction.reseller?.id
      ? selectedTransaction.reseller
      : (selectedTransaction.reseller_id
        ? (resellers.find((r) => r.id === selectedTransaction.reseller_id) || { id: selectedTransaction.reseller_id, name: selectedTransaction.reseller?.name || selectedTransaction.reseller_name })
        : (selectedMerchant?.createdByResellerId
          ? resellers.find((r) => r.id === selectedMerchant.createdByResellerId)
          : null)))
    : null;
  const selectedEngine = null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
              <ArrowDownToLine className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">{t("deposits")}</h1>
              <p className="text-slate-500">
                {txLoading ? (
                  <Skeleton width={200} />
                ) : (
                  `${stats.count} ${t("deposits")} • RS${stats.total.toLocaleString()} ${t("total")}`
                )}
              </p>
            </div>
          </div>
        </div>
        <div className="flex gap-4 text-sm">
          <div className="px-4 py-2 bg-emerald-50 rounded-lg">
            <span className="text-emerald-600 font-medium">
              {txLoading ? <Skeleton width={60} /> : `${stats.successCount} ${t("successful")}`}
            </span>
          </div>
          <div className="px-4 py-2 bg-amber-50 rounded-lg">
            <span className="text-amber-600 font-medium">
              {txLoading ? <Skeleton width={60} /> : `${stats.pendingCount} ${t("pending")}`}
            </span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <TransactionFilters
        filters={filters}
        onFilterChange={(f) => {
          setSearch(f.search);
          setFilters(f);
          setPage(0);
        }}
        onEntityFilterChange={(type, value) => {
          setFilters((prev) => ({ ...prev, [type]: value }));
          setPage(0);
        }}
        merchants={merchants}
        engines={engines}
        resellers={resellers}
        providers={providerOptions}
        onExport={handleExportCsv}
        showTypeFilter={false}
        onReset={() => {
          setFilters({
            search: "",
            type: "all",
            engine: "all",
            reseller: "all",
            status: "all",
            merchant: "all",
            provider: "all",
            currency: "all",
            location: "all",
            dateFrom: undefined,
            dateTo: undefined,
          });
          setMerchantIds([]);
          setSearch("");
          setPage(0);
        }}
      />

      <TransactionTable
        transactions={combinedData}
        isLoading={txLoading}
        onView={setSelectedTransaction}
        mixedTypes={true}
      />

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">
            Showing {page * pageSize + 1} to {Math.min((page + 1) * pageSize, totalTransactions)} of {totalTransactions}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(page - 1)} disabled={page === 0}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm">Page {page + 1} of {totalPages}</span>
            <Button variant="outline" size="sm" onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Transaction Detail Panel */}
      <TransactionDetailPanel
        transaction={selectedTransaction}
        open={!!selectedTransaction}
        onClose={() => setSelectedTransaction(null)}
        engine={selectedEngine}
        reseller={selectedReseller}
        merchant={selectedMerchant}
        isQRCode={selectedTransaction?.type !== "withdrawal"}
      />
    </div>
  );
}